import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ScheduledPostTarget } from 'types'
import { getPlatformValidators } from 'components/ArticlePostPreview/validators'

export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

/**
 * Validates a scheduled post against platform-specific requirements
 * @param scheduledPost - The scheduled post to validate
 * @param platform - The platform to validate against
 * @param target - Optional target to validate (uses target_overrides when present)
 * @returns Validation result with isValid flag and array of error messages
 */
export const validateScheduledPost = (
  scheduledPost: ScheduledPostShow | null | undefined,
  platform: ExternalIntegrationEnum,
  target?: ScheduledPostTarget | null,
): ValidationResult => {
  if (!scheduledPost) {
    return { isValid: true, errors: [] }
  }

  const validators = getPlatformValidators(platform)
  if (!validators) {
    return { isValid: true, errors: [] }
  }

  const errors: string[] = []

  const content = target?.target_overrides?.content || scheduledPost.content

  // Validate content
  if (content) {
    const contentErrors = validators.validateContent(content)
    errors.push(...contentErrors)
  }

  // Validate media files
  if (scheduledPost.media_objects && scheduledPost.media_objects.length > 0) {
    const fileErrors = validators.validateFiles(scheduledPost.media_objects)
    errors.push(...fileErrors)

    const videoSizeErrors = validators.validateVideosSize(scheduledPost.media_objects)
    errors.push(...videoSizeErrors)

    const imageSizeErrors = validators.validateImagesSize(scheduledPost.media_objects)
    errors.push(...imageSizeErrors)

    const videoDurationErrors = validators.validateVideosDuration(scheduledPost.media_objects)
    errors.push(...videoDurationErrors)

    const videoAspectRatioErrors = validators.validateVideosAspectRatio(scheduledPost.media_objects)
    errors.push(...videoAspectRatioErrors)

    const videoFormatErrors = validators.validateVideosFormat(scheduledPost.media_objects)
    errors.push(...videoFormatErrors)

    const videoCountErrors = validators.validateVideosCount(scheduledPost.media_objects)
    errors.push(...videoCountErrors)

    // Optional validators
    if (validators.validateVideosWidth) {
      const videoWidthErrors = validators.validateVideosWidth(scheduledPost.media_objects)
      errors.push(...videoWidthErrors)
    }

    if (validators.validateVideosFrameRate) {
      const videoFrameRateErrors = validators.validateVideosFrameRate(scheduledPost.media_objects)
      errors.push(...videoFrameRateErrors)
    }
  }

  const postSettings = target?.target_overrides?.post_settings || scheduledPost.post_settings

  // Validate post settings
  if (postSettings) {
    const settingsErrors = validators.validatePostSettings(postSettings)
    errors.push(...settingsErrors)
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}
